import {
  convertWeight,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  calculateFreightClass,
  FreightClass,
  HandlingUnitType,
  HandlingUnitTypeLabels,
} from "@redotech/redo-model/freight/freight";
import type { FreightHandlingUnitRow } from "@redotech/redo-model/freight/freight-handling-unit";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  RedoTable,
  RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useMemo } from "react";
import {
  AssignedProduct,
  CUSTOM_HANDLING_UNIT_ID,
  FreightProduct,
  HandlingUnit,
  HandlingUnitPreset,
} from "./freight-booking-types";
import * as styles from "./handling-unit-card.module.css";

const handlingUnitTypeOptions: RedoListItem<HandlingUnitType>[] = Object.values(
  HandlingUnitType,
).map((type) => ({
  type: "text" as const,
  id: type,
  value: type,
  text: HandlingUnitTypeLabels[type],
}));

interface AssignedProductRow {
  product: FreightProduct;
  assignedProduct: AssignedProduct;
  assignedIndex: number;
  maxQuantity: number;
}

interface HandlingUnitCardProps {
  unit: HandlingUnit;
  unitIndex: number;
  products: FreightProduct[];
  handlingUnits: HandlingUnit[];
  savedHandlingUnits: FreightHandlingUnitRow[];
  onUpdate: (index: number, updates: Partial<HandlingUnit>) => void;
  onDelete: (index: number) => void;
  onAssignProducts: (index: number) => void;
}

export const HandlingUnitCard = memo(function HandlingUnitCard({
  unit,
  unitIndex,
  products,
  handlingUnits,
  savedHandlingUnits,
  onUpdate,
  onDelete,
  onAssignProducts,
}: HandlingUnitCardProps) {
  const isCustom = unit.preset.type === "custom";

  const presetOptions = useMemo<RedoListItem<string>[]>(() => {
    const saved = savedHandlingUnits.map((u) => ({
      type: "text" as const,
      id: u.id,
      value: u.id,
      text: u.name,
    }));
    return [
      ...saved,
      {
        type: "text" as const,
        id: CUSTOM_HANDLING_UNIT_ID,
        value: CUSTOM_HANDLING_UNIT_ID,
        text: "Custom",
      },
    ];
  }, [savedHandlingUnits]);

  const selectedPresetOption = useMemo(
    () => presetOptions.find((o) => o.value === unit.preset.id),
    [unit.preset, presetOptions],
  );

  const handlePresetChange = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      if (item.value === CUSTOM_HANDLING_UNIT_ID) {
        const newPreset: HandlingUnitPreset = {
          type: "custom",
          id: CUSTOM_HANDLING_UNIT_ID,
        };
        onUpdate(unitIndex, {
          preset: newPreset,
          type: HandlingUnitType.PALLET,
          dimensions: undefined,
        });
        return;
      }
      const saved = savedHandlingUnits.find((u) => u.id === item.value);
      if (!saved) {
        return;
      }
      const newPreset: HandlingUnitPreset = { type: "saved", id: saved.id };
      onUpdate(unitIndex, {
        preset: newPreset,
        type: saved.type,
        dimensions: {
          length: saved.length,
          width: saved.width,
          height: saved.height,
        },
      });
    },
    [unitIndex, onUpdate, savedHandlingUnits],
  );

  const selectedType = useMemo(
    () => handlingUnitTypeOptions.find((o) => o.value === unit.type),
    [unit.type],
  );

  const productsById = useMemo(() => {
    const map = new Map<string, FreightProduct>();
    for (const p of products) {
      map.set(p.id, p);
    }
    return map;
  }, [products]);

  const assignedElsewhere = useMemo(() => {
    const map = new Map<string, number>();
    for (const [i, handlingUnit] of handlingUnits.entries()) {
      if (i === unitIndex) {
        continue;
      }
      for (const ap of handlingUnit.assignedProducts) {
        map.set(ap.productId, (map.get(ap.productId) ?? 0) + ap.quantity);
      }
    }
    return map;
  }, [handlingUnits, unitIndex]);

  const assignedRows: AssignedProductRow[] = useMemo(
    () =>
      unit.assignedProducts
        .map((ap, idx) => {
          const product = productsById.get(ap.productId);
          if (!product) {
            return undefined;
          }
          const otherAssigned = assignedElsewhere.get(ap.productId) ?? 0;
          const maxQuantity = product.quantity - otherAssigned;
          return {
            product,
            assignedProduct: ap,
            assignedIndex: idx,
            maxQuantity,
          };
        })
        .filter((row): row is AssignedProductRow => row !== undefined),
    [unit.assignedProducts, productsById, assignedElsewhere],
  );

  const totalAssignedQuantity = useMemo(
    () => unit.assignedProducts.reduce((sum, ap) => sum + ap.quantity, 0),
    [unit.assignedProducts],
  );

  const calculatedFreightClass: FreightClass | undefined = useMemo(() => {
    if (
      !unit.dimensions ||
      unit.dimensions.length <= 0 ||
      unit.dimensions.width <= 0 ||
      unit.dimensions.height <= 0
    ) {
      return undefined;
    }
    const totalWeightLbs = unit.assignedProducts.reduce((sum, ap) => {
      const product = productsById.get(ap.productId);
      if (!product) {
        return sum;
      }
      return (
        sum +
        convertWeight(product.itemWeight, WeightUnit.POUND).value * ap.quantity
      );
    }, 0);
    if (totalWeightLbs <= 0) {
      return undefined;
    }
    return calculateFreightClass({
      weight: { value: totalWeightLbs, unit: WeightUnit.POUND },
      dimensionsInInches: unit.dimensions,
      numberOfHandlingUnits: unit.quantity,
    });
  }, [unit.dimensions, unit.quantity, unit.assignedProducts, productsById]);

  useEffect(() => {
    if (calculatedFreightClass !== unit.freightClass) {
      onUpdate(unitIndex, { freightClass: calculatedFreightClass });
    }
  }, [calculatedFreightClass, unit.freightClass, unitIndex, onUpdate]);

  const updateAssignedProduct = useCallback(
    (assignedIndex: number, quantity: number) => {
      const newAssigned = [...unit.assignedProducts];
      newAssigned[assignedIndex] = { ...newAssigned[assignedIndex]!, quantity };
      onUpdate(unitIndex, { assignedProducts: newAssigned });
    },
    [unit.assignedProducts, unitIndex, onUpdate],
  );

  const removeAssignedProduct = useCallback(
    (assignedIndex: number) => {
      const newAssigned = unit.assignedProducts.filter(
        (_, i) => i !== assignedIndex,
      );
      onUpdate(unitIndex, { assignedProducts: newAssigned });
    },
    [unit.assignedProducts, unitIndex, onUpdate],
  );

  interface AssignedSummary {
    totalQuantity: number;
  }

  const summary: AssignedSummary = useMemo(
    () => ({ totalQuantity: totalAssignedQuantity }),
    [totalAssignedQuantity],
  );

  const columns: RedoTableColumn<AssignedProductRow, AssignedSummary>[] =
    useMemo(
      () => [
        {
          widthMode: { type: "fill" as const, relativeLength: 7 },
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="tertiary">
                Product
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="xs">
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  {row.product.name}
                  {row.product.sku ? " - " : ""}
                </Text>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {row.product.sku ?? ""}
                </Text>
              </Flex>
            </RedoTablePaddedCell>
          ),
          renderSummaryCell: () => (
            <RedoTableTextCell fontWeight="semibold" text="Total" />
          ),
        },
        {
          widthMode: { type: "fill" as const },
          alignment: "center" as const,
          renderHeaderCell: () => (
            <RedoTablePaddedCell>
              <Text fontSize="xxs" textColor="tertiary">
                Quantity
              </Text>
            </RedoTablePaddedCell>
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="xs" justify="flex-end">
                <RedoIncrementDecrement
                  max={row.maxQuantity}
                  min={1}
                  setValue={(val) =>
                    updateAssignedProduct(row.assignedIndex, val)
                  }
                  size="small"
                  value={row.assignedProduct.quantity}
                />
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={XCloseIcon}
                  onClick={() => removeAssignedProduct(row.assignedIndex)}
                  size="xs"
                />
              </Flex>
            </RedoTablePaddedCell>
          ),
          renderSummaryCell: ({ summary }) => (
            <RedoTableTextCell
              fontWeight="semibold"
              text={String(summary.totalQuantity)}
            />
          ),
        },
      ],
      [updateAssignedProduct, removeAssignedProduct],
    );

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderWidth="1px"
      dir="column"
      gap="2xl"
      pb={assignedRows.length > 0 ? undefined : "2xl"}
      pt="2xl"
      radius="sm"
    >
      <Flex align="center" justify="space-between" px="2xl">
        <Flex align="center" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            Unit {unitIndex + 1}
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            Class {calculatedFreightClass ?? "Unknown"}
          </Text>
        </Flex>
        <Flex align="center" gap="sm">
          <RedoButton
            hierarchy="secondary"
            onClick={() => onAssignProducts(unitIndex)}
            size="sm"
            text="+ Assign products"
          />
          <RedoButton
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={() => onDelete(unitIndex)}
            size="sm"
            theme="destructive"
          />
        </Flex>
      </Flex>
      <Flex align="flex-end" gap="2xl" px="2xl" wrap="wrap">
        <Flex grow={1}>
          <RedoSingleSelectDropdownInput
            label="Preset"
            options={presetOptions}
            optionSelected={handlePresetChange}
            placeholder="Select a preset"
            selectedItem={selectedPresetOption}
            size="xs"
          />
        </Flex>
        <Flex grow={1}>
          <RedoSingleSelectDropdownInput
            disabled={!isCustom}
            label="Type"
            options={handlingUnitTypeOptions}
            optionSelected={({ item }) =>
              onUpdate(unitIndex, { type: item.value })
            }
            placeholder="Select type"
            selectedItem={selectedType}
            size="xs"
          />
        </Flex>
        <RedoIncrementDecrement
          label="Quantity"
          min={1}
          setValue={(val) => onUpdate(unitIndex, { quantity: val })}
          size="small"
          value={unit.quantity}
        />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Dimensions (L x W x H in inches)
          </Text>
          <Flex align="center" gap="xs">
            <RedoIncrementDecrement
              disabled={!isCustom}
              min={0}
              setValue={(val) =>
                onUpdate(unitIndex, {
                  dimensions: {
                    length: val,
                    width: unit.dimensions?.width ?? 0,
                    height: unit.dimensions?.height ?? 0,
                  },
                })
              }
              size="small"
              value={unit.dimensions?.length ?? 0}
            />
            <RedoIncrementDecrement
              disabled={!isCustom}
              min={0}
              setValue={(val) =>
                onUpdate(unitIndex, {
                  dimensions: {
                    length: unit.dimensions?.length ?? 0,
                    width: val,
                    height: unit.dimensions?.height ?? 0,
                  },
                })
              }
              size="small"
              value={unit.dimensions?.width ?? 0}
            />
            <RedoIncrementDecrement
              disabled={!isCustom}
              min={0}
              setValue={(val) =>
                onUpdate(unitIndex, {
                  dimensions: {
                    length: unit.dimensions?.length ?? 0,
                    width: unit.dimensions?.width ?? 0,
                    height: val,
                  },
                })
              }
              size="small"
              value={unit.dimensions?.height ?? 0}
            />
          </Flex>
        </Flex>
        <Flex alignSelf="flex-start" dir="column" gap="md">
          <Text fontSize="xs" textColor="primary">
            Stackable
          </Text>
          <RedoCheckbox
            setValue={(val: CheckboxValue) =>
              onUpdate(unitIndex, { stackable: val === true })
            }
            size="sm"
            value={unit.stackable}
          />
        </Flex>
      </Flex>
      {assignedRows.length > 0 && (
        <RedoTable<AssignedProductRow, AssignedSummary>
          additionalClasses={styles.table}
          layout={{ columns }}
          rowsOfData={assignedRows}
          size="sm"
          summary={{ data: summary, position: "bottom" }}
        />
      )}
    </Flex>
  );
});
